/**
 * 모델명을 개발자용 닉네임으로 변환
 * 사용자에게 실제 모델명을 숨기기 위해 사용
 */

// 모델명 → 닉네임 매핑 (개발자만 알 수 있도록)
const MODEL_NICKNAME_MAP = {
  // Gemini 3.x 모델
  "gemini-3.7-flash": "g37f", // 현행 운영 Flash
  "gemini-3-flash-preview": "g3f", // ⭐ Gemini 3.0 Flash (2025.12.17 출시, 속도+추론)
  "gemini-3.1-pro-preview": "g31p", // ⭐ Gemini 3.1 Pro (2026.02.19 출시, 최신 Pro)
  "gemini-3.5-flash-preview": "g35f", // ⭐ Gemini 3.5 Flash (2026.05 출시, 최신 Flash)
  "gemini-3-pro-preview": "g3p", // ⭐ Gemini 3.0 Pro (2025.11.18 출시, 추론 우선)
  // DeepSeek 모델
  deepseek: "ds",
  // 🧠 Fine-tuned 모델
  "docuai-ft-invoice": "ft-inv",
  // 🌐 OpenRouter Qwen
  "qwen3-vl-32b": "qw3v",
  // 🎯 Vast.ai vLLM (Qwen3.6-27B + 듀얼 LoRA, 파일 확장자 자동 라우팅)
  "qwen36-kyungin": "qw36", // xlsx → text 어댑터, PDF/JPG → vision 어댑터 (서버에서 자동 분기)
  "qwen36-damin-import": "qw36-di", // 🎯 다민 수입 인보이스 v1 (damin-import-v1, 1K 학습)
  "qwen36-damin-import-v2": "qw36-di2", // 🎯 다민 수입 인보이스 v2 (damin-import-v2, 24K 학습, items 압축버그 — v3 권장)
  "qwen36-damin-import-v3": "qw36-di3", // 🎯 다민 수입 인보이스 v3 (damin-import-v3, GT 정상화 — items 행단위 보존)
  "minimax-m2.5": "mm25",
  // 🤖 OpenAI GPT 모델
  "gpt-4.1-mini": "g41m",
  "gpt-4.1": "g41",
  // PaddleOCR
  "paddleocr-label": "pocr",
  // Extend.ai
  "extend-ai": "ext",
};

// 닉네임 → 모델명 매핑 (역변환용)
const NICKNAME_MODEL_MAP = Object.fromEntries(
  Object.entries(MODEL_NICKNAME_MAP).map(([model, nickname]) => [
    nickname,
    model,
  ]),
);

/**
 * 모델명을 닉네임으로 변환
 * @param {string} modelName - 원본 모델명 (예: 'gemini-2.5-flash')
 * @returns {string} 닉네임 (예: 'g2')
 */
export function getModelNickname(modelName) {
  if (!modelName) return "";
  return MODEL_NICKNAME_MAP[modelName] || modelName;
}

/**
 * 닉네임을 모델명으로 변환 (관리자용)
 * @param {string} nickname - 닉네임 (예: 'g2')
 * @returns {string} 원본 모델명 (예: 'gemini-2.5-flash')
 */
export function getModelName(nickname) {
  if (!nickname) return "";
  return NICKNAME_MODEL_MAP[nickname] || nickname;
}

/**
 * 모델명 표시 (UI용)
 * @param {string} modelName - 원본 모델명
 * @param {boolean} showFullName - true면 원본 모델명, false면 닉네임 (기본값: false)
 * @returns {string} 표시할 모델명
 */
export function formatModelName(modelName, showFullName = false) {
  if (!modelName) return "";
  return showFullName ? modelName : getModelNickname(modelName);
}

/**
 * source 필드에서 모델명 추출 및 닉네임 변환
 * @param {string|object} source - source 필드 (문자열 또는 객체)
 * @returns {string} 닉네임
 */
export function getSourceNickname(source) {
  if (!source) return "";

  // source가 객체인 경우
  if (typeof source === "object") {
    const modelName = source.model || source.name || "";
    return getModelNickname(modelName);
  }

  // source가 문자열인 경우
  return getModelNickname(source);
}

/**
 * 모든 모델 닉네임 매핑 반환 (설정 화면용)
 * @returns {Object} { modelName: nickname }
 */
export function getAllModelNicknames() {
  return { ...MODEL_NICKNAME_MAP };
}
